import type { Ecriture, Balance } from '@shared/types/Ecriture';
import { BalanceService } from './BalanceService';
import { estCompteContraActif } from './referentiel/ComptesAlias';
import { SigSyscohadaService } from './referentiel/SigSyscohada';
import type { SoldesIntermediairesGestion } from './referentiel/SigSyscohada';

export interface BilanResult {
  actif_immobilise: number;
  actif_circulant: number;
  tresorerie_actif: number;
  /**
   * Montant de la perte de l'exercice (≥ 0), à titre INFORMATIF uniquement.
   * SYSCOHADA : une perte ne figure jamais à l'actif ; elle réduit les capitaux propres (voir `resultat_net`).
   */
  resultat_deficit: number;
  /** Actif immobilisé + circulant + trésorerie actif (la perte n'y est jamais ajoutée). */
  total_actif: number;
  /**
   * Total des capitaux propres, résultat net de l'exercice inclus (négatif en cas de perte) :
   * `capitaux_propres_hors_resultat + resultat_net`.
   */
  capitaux_propres: number;
  /** Capitaux propres avant résultat de l'exercice (comptes 10 à 15 : capital, réserves, report à nouveau…). */
  capitaux_propres_hors_resultat: number;
  /** Résultat net de l'exercice : produits − charges (bénéfice > 0, perte < 0). */
  resultat_net: number;
  /** Compte de présentation du résultat net (131 bénéfice / 139 perte par défaut, paramétrable). */
  compte_resultat_net: string;
  /** Bénéfice de l'exercice (≥ 0), à titre informatif ; déjà inclus dans `capitaux_propres`. */
  resultat_benefice: number;
  dettes_long_terme: number;
  /** Compte 19 — provisions pour risques et charges. */
  provisions_risques: number;
  dettes_court_terme: number;
  tresorerie_passif: number;
  total_passif: number;
  equilibre: boolean;
  /** Écart |actif − passif| en FCFA. */
  ecart: number;
}

/** Comptes SYSCOHADA révisé de présentation du résultat net de l'exercice (classe 13). */
export const COMPTE_RESULTAT_NET_BENEFICE = '131';
export const COMPTE_RESULTAT_NET_PERTE = '139';

export interface BilanOptions {
  /** Numéro du compte « Résultat net : bénéfice » (défaut 131). */
  compteBenefice?: string;
  /** Numéro du compte « Résultat net : perte » (défaut 139). */
  comptePerte?: string;
}

export interface CompteResultatResult {
  produits_exploitation: number;
  charges_exploitation: number;
  resultat_exploitation: number;
  produits_financiers: number;
  charges_financieres: number;
  resultat_financier: number;
  produits_HAO: number;
  charges_HAO: number;
  impots: number;
  resultat_net: number;
}

/**
 * Calcul du Bilan et du Compte de Résultat SYSCOHADA simplifiés.
 *
 * Classes bilan :
 * - Actif = 2 + soldes débiteurs 3/4 + soldes débiteurs 5
 * - Passif = 1 + soldes créditeurs 2/3/4/5 + résultat net de l'exercice
 * Le résultat net des classes 6/7 (produits − charges) est présenté dans les CAPITAUX PROPRES, au passif :
 * positif en cas de bénéfice (compte 131), négatif en cas de perte (compte 139). Une perte ne figure JAMAIS à
 * l'actif (AUDCIF 2017, bilan SYSCOHADA révisé ; recette 1.1.6, défaut n° 24, décision D6). Actif = Passif dès que
 * le journal est équilibré (Σ débit = Σ crédit).
 */
export class EtatsFinanciersService {
  static calculerBilan(ecritures: readonly Ecriture[], options: BilanOptions = {}): BilanResult {
    const bal: Balance = BalanceService.calculer(ecritures);
    let actif_immobilise = 0,
      actif_circulant = 0,
      tresorerie_actif = 0;
    let capitaux_propres = 0,
      dettes_long_terme = 0,
      provisions_risques = 0,
      dettes_court_terme = 0,
      tresorerie_passif = 0;

    for (const ligne of Object.values(bal)) {
      const cls = ligne.compte.charAt(0);
      const racine = ligne.compte.slice(0, 2);
      const solde = ligne.solde;
      if (cls === '2') {
        if (estCompteContraActif(ligne.compte)) actif_immobilise += solde;
        else if (solde > 0) actif_immobilise += solde;
        else dettes_long_terme += -solde;
      } else if (cls === '3') {
        actif_circulant += solde;
      } else if (cls === '4') {
        if (estCompteContraActif(ligne.compte)) actif_circulant += solde;
        else if (solde > 0) actif_circulant += solde;
        else dettes_court_terme += -solde;
      } else if (cls === '5') {
        if (solde > 0) tresorerie_actif += solde;
        else tresorerie_passif += -solde;
      } else if (cls === '1') {
        if (racine === '16' || racine === '17' || racine === '18') dettes_long_terme += -solde;
        else if (racine === '19') provisions_risques += -solde;
        else capitaux_propres += -solde;
      }
    }

    const cr = EtatsFinanciersService.calculerCompteResultat(ecritures);
    const resultat_net = cr.resultat_net;
    const resultat_benefice = resultat_net > 0 ? resultat_net : 0;
    const resultat_deficit = resultat_net < 0 ? -resultat_net : 0;
    const capitaux_propres_hors_resultat = capitaux_propres;
    // Le résultat net (positif ou négatif) est une ligne des capitaux propres : jamais un élément d'actif.
    capitaux_propres += resultat_net;
    const compte_resultat_net =
      resultat_net < 0
        ? (options.comptePerte ?? COMPTE_RESULTAT_NET_PERTE)
        : (options.compteBenefice ?? COMPTE_RESULTAT_NET_BENEFICE);

    const total_actif = actif_immobilise + actif_circulant + tresorerie_actif;
    const total_passif =
      capitaux_propres +
      dettes_long_terme +
      provisions_risques +
      dettes_court_terme +
      tresorerie_passif;
    const ecart = Math.abs(total_actif - total_passif);
    const equilibre = ecart <= 1;

    if (!equilibre) {
      console.error(
        `[bilan] Déséquilibre OHADA : actif=${total_actif} passif=${total_passif} écart=${ecart}`,
      );
    }

    return {
      actif_immobilise,
      actif_circulant,
      tresorerie_actif,
      resultat_deficit,
      total_actif,
      capitaux_propres,
      capitaux_propres_hors_resultat,
      resultat_net,
      compte_resultat_net,
      resultat_benefice,
      dettes_long_terme,
      provisions_risques,
      dettes_court_terme,
      tresorerie_passif,
      total_passif,
      equilibre,
      ecart,
    };
  }

  /** Refuse l’export si le bilan n’est pas équilibré à ±1 FCFA. */
  static assertEquilibrePourExport(bilan: BilanResult): void {
    if (!bilan.equilibre) {
      throw new Error(
        `Bilan déséquilibré (écart ${bilan.ecart} FCFA) — export bloqué. Vérifiez les écritures de la période.`,
      );
    }
  }

  static calculerCompteResultat(ecritures: readonly Ecriture[]): CompteResultatResult {
    let produits_exploitation = 0,
      charges_exploitation = 0;
    let produits_financiers = 0,
      charges_financieres = 0;
    let produits_HAO = 0,
      charges_HAO = 0;
    let impots = 0;

    for (const e of ecritures) {
      const compte = e.compte;
      const flux = e.credit - e.debit;
      const cls = compte.charAt(0);
      if (cls === '7') {
        if (compte.startsWith('77')) produits_financiers += flux;
        else if (compte.startsWith('84') || compte.startsWith('85')) produits_HAO += flux;
        else produits_exploitation += flux;
      } else if (cls === '6') {
        const inverse = -flux;
        if (compte.startsWith('67')) charges_financieres += inverse;
        else if (compte.startsWith('83') || compte.startsWith('85')) charges_HAO += inverse;
        else if (compte.startsWith('695') || compte.startsWith('89')) impots += inverse;
        else charges_exploitation += inverse;
      } else if (cls === '8') {
        if (compte.startsWith('82') || compte.startsWith('84') || compte.startsWith('86') || compte.startsWith('88')) {
          produits_HAO += flux;
        } else if (!compte.startsWith('87') && !compte.startsWith('89')) {
          charges_HAO += -flux;
        } else if (compte.startsWith('89')) {
          impots += -flux;
        }
      }
    }

    const resultat_exploitation = produits_exploitation - charges_exploitation;
    const resultat_financier = produits_financiers - charges_financieres;
    const resultat_net =
      resultat_exploitation + resultat_financier + (produits_HAO - charges_HAO) - impots;

    return {
      produits_exploitation,
      charges_exploitation,
      resultat_exploitation,
      produits_financiers,
      charges_financieres,
      resultat_financier,
      produits_HAO,
      charges_HAO,
      impots,
      resultat_net,
    };
  }

  /** SIG normalisés SYSCOHADA (marge, VA, EBE, RAO, HAO). */
  static calculerSIG(ecritures: readonly Ecriture[]): SoldesIntermediairesGestion {
    return SigSyscohadaService.calculer(ecritures);
  }
}
